import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Chip, // Added Chip import
  CircularProgress,
  Slider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import { Category } from '../../types/Category';
import api from '../../services/api';
import AiSparklesIcon from '../icons/AiSparklesIcon';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { useHelpContent } from '../../utils/HelpUtils';
import { normalizeCategoryName } from '../../utils/CategoryUtils';

interface FindCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  selectedCategory: Category | null;
  onCategorySelect: (category: Category) => void;
  onExpandParents: (category: Category) => void;
  findCategoryInTree: (categories: Category[], targetId: string) => Category | null;
  onContentSelect?: (categoryId: string, contentId: string, searchString: string, searchTerms: string[], searchMode: 'any' | 'all' | 'exact' | 'semantic', wholeWord: boolean, caseSensitive: boolean, allMatchingContentIds?: string[]) => void;
  subcaseGroups?: { name: string; subcases: string[] }[];
  documentSubcases?: string[];
  documentName?: string;
}

const FindCategoryDialog: React.FC<FindCategoryDialogProps> = ({
  open,
  onClose,
  categories,
  selectedCategory,
  onCategorySelect,
  onExpandParents,
  findCategoryInTree,
  onContentSelect,
  subcaseGroups = [],
  documentSubcases = [],
  documentName,
}) => {
  const { settings, updateSettings } = useSearchSettings();
  const { findSimilarityThreshold, findShowSimilarityScore } = settings;
  const { getHelpText, getTooltipTextForState } = useHelpContent();
  
  // Find feature state
  const [findSearchString, setFindSearchString] = useState('');
  const [findResults, setFindResults] = useState<{ category: Category; matchedContents?: any[]; score?: number }[]>([]);
  const [currentFindIndex, setCurrentFindIndex] = useState(0);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [findStarted, setFindStarted] = useState(false);
  const [showEndWarning, setShowEndWarning] = useState(false);
  const [showStartWarning, setShowStartWarning] = useState(false);
  const [hasWrappedAround, setHasWrappedAround] = useState(false);
  const [searchInDescriptions, setSearchInDescriptions] = useState(false);
  const [searchInNames, setSearchInNames] = useState(true); // Default to true since names are usually the most important
  const [searchMode, setSearchMode] = useState<'any' | 'all' | 'exact' | 'semantic'>('any');
  const [wholeWord, setWholeWord] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [searchInContent, setSearchInContent] = useState(false);
  // Add a state to store the current search's matching content IDs
  const [currentSearchMatchingIds, setCurrentSearchMatchingIds] = useState<string[]>([]);
  
  // Subcase filtering state (same model as Show Sub Tree: one list + active group chips)
  const [filterBySubcases, setFilterBySubcases] = useState(false);
  const [selectedSubcaseGroups, setSelectedSubcaseGroups] = useState<string[]>([]);
  const [selectedIndividualSubcases, setSelectedIndividualSubcases] = useState<string[]>([]);
  const findFilterSubcasesBaselineRef = useRef<string[] | null>(null);

  // UI state
  const [searchOptionsCollapsed, setSearchOptionsCollapsed] = useState(false);
  const [subcaseSelectionCollapsed, setSubcaseSelectionCollapsed] = useState(false);
  const [isSemanticSearching, setIsSemanticSearching] = useState(false);
  const [showFindSettings, setShowFindSettings] = useState(false);
  
  // Dialog position and drag state
  const [dialogPosition, setDialogPosition] = useState({ x: window.innerWidth - 350, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Helper function to parse search terms based on mode
  const parseSearchTerms = (searchString: string): string[] => {
    if (searchMode === 'exact') {
      return [searchString];
    }
    // Split by whitespace for 'any' and 'all' modes
    return searchString.split(/\s+/).filter(term => term.length > 0);
  };
  
  // Helper function to check if text matches search terms
  const checkTextMatch = (text: string, searchTerms: string[]): boolean => {
    // For case-sensitive search, use original text; otherwise convert to lowercase
    const searchText = caseSensitive ? text : text.toLowerCase();
    
    if (searchMode === 'exact') {
      const searchTerm = searchTerms[0];
      if (wholeWord) {
        // Unicode-aware word boundary
        const searchPattern = caseSensitive ? searchTerm : searchTerm.toLowerCase();
        const escapedPattern = searchPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
        return wordBoundary.test(searchText);
      } else {
        const searchPattern = caseSensitive ? searchTerm : searchTerm.toLowerCase();
        return searchText.includes(searchPattern);
      }
    }
    
    if (searchMode === 'all') {
      // All terms must be found
      return searchTerms.every(term => {
        if (wholeWord) {
          const searchPattern = caseSensitive ? term : term.toLowerCase();
          const escapedPattern = searchPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
          return wordBoundary.test(searchText);
        } else {
          const searchPattern = caseSensitive ? term : term.toLowerCase();
          return searchText.includes(searchPattern);
        }
      });
    }
    
    // 'any' mode - at least one term must be found
    return searchTerms.some(term => {
      if (wholeWord) {
        const searchPattern = caseSensitive ? term : term.toLowerCase();
        const escapedPattern = searchPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordBoundary = new RegExp(`(?<=^|[^\\p{L}\\p{N}])${escapedPattern}(?=[^\\p{L}\\p{N}]|$)`, caseSensitive ? 'gu' : 'giu');
        return wordBoundary.test(searchText);
      } else {
        const searchPattern = caseSensitive ? term : term.toLowerCase();
        return searchText.includes(searchPattern);
      }
    });
  };

  const subcasesUnionForSubcaseGroupNames = (names: string[]) => {
    const acc = new Set<string>();
    names.forEach(n => {
      const g = subcaseGroups.find(x => x.name === n);
      g?.subcases.forEach(s => acc.add(s));
    });
    return acc;
  };

  const handleFindSubcaseGroupSelect = (groupName: string) => {
    const group = subcaseGroups.find(g => g.name === groupName);
    if (!group) return;

    const allDocSubcasesSelected = (selected: string[]) =>
      documentSubcases.length > 0 &&
      selected.length === documentSubcases.length &&
      documentSubcases.every(s => selected.includes(s));

    setSelectedSubcaseGroups(prevNames => {
      const turningOff = prevNames.includes(groupName);
      const nextNames = turningOff
        ? prevNames.filter(n => n !== groupName)
        : [...prevNames, groupName];

      setSelectedIndividualSubcases(prevSub => {
        if (!turningOff) {
          if (prevNames.length === 0) {
            findFilterSubcasesBaselineRef.current = [...prevSub];
          }
          if (allDocSubcasesSelected(prevSub) && prevNames.length === 0) {
            return [...group.subcases];
          }
          const merged = [...prevSub];
          for (const s of group.subcases) {
            if (merged.indexOf(s) === -1) merged.push(s);
          }
          return merged;
        }
        const cover = subcasesUnionForSubcaseGroupNames(nextNames);
        const filtered = prevSub.filter(s => !group.subcases.includes(s) || cover.has(s));
        if (nextNames.length === 0) {
          if (findFilterSubcasesBaselineRef.current !== null) {
            const restored = [...findFilterSubcasesBaselineRef.current];
            findFilterSubcasesBaselineRef.current = null;
            return restored;
          }
          return filtered.length === 0 ? [...documentSubcases] : filtered;
        }
        return filtered;
      });

      return nextNames;
    });
  };

  /** Effective subcase filter list (group chips keep this in sync, same as Show Sub Tree). */
  const getSelectedSubcases = (): string[] => selectedIndividualSubcases;

  // Helper function to check if content should be included based on subcase filter
  const shouldIncludeContent = (content: any): boolean => {
    if (!filterBySubcases) return true;

    const selectedSubcases = getSelectedSubcases();
    if (selectedSubcases.length === 0) return false;

    return selectedSubcases.includes(content.subcase);
  };

  // Perform breadth-first search
  const performBreadthFirstSearch = (startCategory: Category, searchString: string): { category: Category; matchedContents?: any[] }[] => {
    console.log('performBreadthFirstSearch called with searchString:', searchString);
    console.log('searchInContent:', searchInContent);
    
    const results: { category: Category; matchedContents?: any[] }[] = [];
    const queue: Category[] = [startCategory];
    const searchTerms = parseSearchTerms(searchString);
    
    while (queue.length > 0) {
      const currentCategory = queue.shift()!;
      
      // Check if current category name contains the search terms (when enabled)
      const nameMatches = searchInNames && checkTextMatch(currentCategory.name, searchTerms);
      
      // Check if description contains the search terms (when enabled)
      const descriptionMatches = searchInDescriptions && 
        currentCategory.description && 
        checkTextMatch(currentCategory.description, searchTerms);
      
      // Check if content contains the search terms (when enabled)
      let matchedContents: any[] = [];
      if (searchInContent && currentCategory.contents && currentCategory.contents.length > 0) {
        console.log('Checking content for category:', currentCategory.name);
        console.log('Content items:', currentCategory.contents.length);

        // Limit content checking to prevent performance issues
        const maxContentToCheck = 50;
        const contentToCheck = currentCategory.contents.slice(0, maxContentToCheck);
        
        for (const content of contentToCheck) {
          // Apply subcase filter
          if (!shouldIncludeContent(content)) {
            continue;
          }
          
          if (checkTextMatch(content.text, searchTerms)) {
            console.log('Found content match!');
            matchedContents.push(content);
            
            // Limit matched contents per category
            if (matchedContents.length >= 10) break;
          }
        }
      }
      
      if (nameMatches || descriptionMatches || matchedContents.length > 0) {
        console.log('Adding result for category:', currentCategory.name, 'matchedContents:', matchedContents.length);
        results.push({ 
          category: currentCategory, 
          matchedContents: matchedContents.length > 0 ? matchedContents : undefined 
        });
      }
      
      // Add children to queue for breadth-first search
      if (currentCategory.children) {
        queue.push(...currentCategory.children);
      }
    }
    
    console.log('Search results:', results);
    return results;
  };

  const handleFindSearch = async () => {
    console.log('handleFindSearch called');
    console.log('findSearchString:', findSearchString);
    console.log('searchInNames:', searchInNames);
    console.log('searchInContent:', searchInContent);
    console.log('searchInDescriptions:', searchInDescriptions);
    console.log('searchMode:', searchMode);
    
    if (!findSearchString.trim()) return;
    
    if (searchMode === 'semantic') {
      // Handle semantic search
      setIsSemanticSearching(true);
      try {
        // Get current document name from props or use default
        const currentDocument = documentName || 'default';
        console.log('Performing semantic search for document:', currentDocument);
        
        const response = await api.post('/search/semantic', {
          question: findSearchString
        }, { params: { documentId: currentDocument} });
        
        console.log('Semantic search response:', response.data);
        
        const semanticResults = response.data.results || [];
        const convertedResults: { category: Category; matchedContents?: any[]; score?: number }[] = [];
        
        for (const result of semanticResults) {
          // Parse contentId to extract category information
          // contentId format: "2:39:עדכון 24 ראיונות:﻿אורח חיים יהודי בבית:﻿חגי ישראל:0"
          // Handle both quoted and unquoted contentId strings
          let contentId = result.contentId;
          try {
            // Try to parse as JSON in case it's a quoted string
            const parsed = JSON.parse(contentId);
            if (typeof parsed === 'string') {
              contentId = parsed;
            }
          } catch (e) {
            // If parsing fails, use the original contentId (it's already a clean string)
          }
          
          const contentIdParts = contentId.split(':');
          if (contentIdParts.length >= 4) {
            // Extract category path (everything except the last part which is the content index)
            const categoryPathParts = contentIdParts.slice(2, -1);
            console.log('Looking for category path parts:', categoryPathParts);
            
            // Find the category in the tree by navigating through the path
            const findCategoryByPathParts = (cats: Category[], pathParts: string[]): Category | null => {
              if (pathParts.length === 0) return null;
              
              const currentPart = pathParts[0];
              const remainingParts = pathParts.slice(1);
              
              for (const cat of cats) {
                if (normalizeCategoryName(cat.name) === normalizeCategoryName(currentPart)) {
                  if (remainingParts.length === 0) {
                    // Found the target category
                    return cat;
                  } else {
                    // Continue searching in children
                    if (cat.children) {
                      const found = findCategoryByPathParts(cat.children, remainingParts);
                      if (found) return found;
                    }
                  }
                }
              }
              return null;
            };
            
            const category = findCategoryByPathParts(categories, categoryPathParts);
            if (category) {
              console.log('Found category:', category.name);
              
              // Find the real content object in the category's contents array
              let realContent = null;
              if (category.contents && category.contents.length > 0) {
                // Extract the content index from the contentId (last part)
                const contentIndex = parseInt(contentIdParts[contentIdParts.length - 1]);
                console.log('Looking for content at index:', contentIndex);
                
                if (contentIndex >= 0 && contentIndex < category.contents.length) {
                  realContent = category.contents[contentIndex];
                  console.log('Found real content:', realContent);
                } else {
                  console.log('Content index out of bounds:', contentIndex, 'vs', category.contents.length);
                }
              }
              
              if (realContent) {
                convertedResults.push({
                  category,
                  matchedContents: [realContent],
                  score: result.score // Store the similarity score
                });
              } else {
                console.log('Real content not found, skipping this result');
              }
            } else {
              console.log('Category not found for path parts:', categoryPathParts);
            }
          }
        }

        // After processing all semantic results, filter by subcases if enabled
        if (filterBySubcases) {
          const selectedSubcases = getSelectedSubcases();
          console.log('Filtering semantic results by subcases:', selectedSubcases);
          const filteredResults =
            selectedSubcases.length === 0
              ? []
              : convertedResults.filter(result => {
                  if (result.matchedContents && result.matchedContents.length > 0) {
                    return result.matchedContents.some(content =>
                      selectedSubcases.includes(content.subcase)
                    );
                  }
                  return false;
                });
          console.log('Filtered semantic results:', filteredResults.length, 'out of', convertedResults.length);
          convertedResults.length = 0;
          convertedResults.push(...filteredResults);
        }
        
        console.log('Converted semantic results:', convertedResults);
        // After creating convertedResults, sort them to match the UI insertion order
        convertedResults.sort((a, b) => {
          const categoryA = a.category;
          const categoryB = b.category;

          // Add null checks for matchedContents
          if (!a.matchedContents || !b.matchedContents || a.matchedContents.length === 0 || b.matchedContents.length === 0) {
            return 0; // Keep original order if no matched contents
          }

          const contentA = a.matchedContents[0];
          const contentB = b.matchedContents[0];

          // If they're in the same category, sort by their position in the contents array
          if (categoryA.id === categoryB.id) {
            // Add null checks for category contents
            if (!categoryA.contents || !categoryB.contents) {
              return 0;
            }

            const indexA = categoryA.contents.findIndex(c => c.id === contentA.id);
            const indexB = categoryB.contents.findIndex(c => c.id === contentB.id);
            return indexA - indexB;
          }

          // If they're in different categories, keep the original order
          return 0;
        });

        setFindResults(convertedResults);
        setCurrentFindIndex(0);
        setCurrentContentIndex(0);
        setFindStarted(true);
        setShowEndWarning(false);
        setShowStartWarning(false);
        setHasWrappedAround(false);
        
        if (convertedResults.length > 0) {
          console.log(convertedResults);
          // Select the first result and expand its parents to make it visible
          const targetResult = convertedResults[0];
          console.log('First semantic result:', targetResult);
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          // Select content if there's a match
          if (targetResult.matchedContents && onContentSelect) {
            console.log('Calling onContentSelect with semantic matched content');

            // Get all matching content IDs from the current semantic results
            const allIds: string[] = [];
            convertedResults.forEach(result => {
              if (result.matchedContents) {
                result.matchedContents.forEach(content => {
                  allIds.push(content.id);
                });
              }
            });

            // Store for navigation
            setCurrentSearchMatchingIds(allIds);

            onContentSelect(
              targetResult.category.id,
              targetResult.matchedContents[0].id,
              findSearchString,
              [],
              searchMode,
              wholeWord,
              caseSensitive,
              allIds
            );
          }
        } else {
          // Show warning in dialog instead of alert
          setShowEndWarning(true);
          setTimeout(() => setShowEndWarning(false), 3000);
        }
        
      } catch (error) {
        console.error('Error performing semantic search:', error);
        setShowEndWarning(true);
        setTimeout(() => setShowEndWarning(false), 3000);
      } finally {
        setIsSemanticSearching(false);
      }
      return;
    }
    
    // Determine starting category: selected category or root
    const startCategory = selectedCategory || categories[0];
    console.log('Starting category:', startCategory?.name);
    if (!startCategory) return;
    
    // Perform breadth-first search
    const results = performBreadthFirstSearch(startCategory, findSearchString);
    console.log('Search completed, results:', results);
    setFindResults(results);
    setCurrentFindIndex(0);
    setFindStarted(true);
    setShowEndWarning(false);
    setShowStartWarning(false);
    setHasWrappedAround(false);

    if (results.length > 0) {
      // Select the first result and expand its parents to make it visible
      const targetResult = results[0];
      console.log('First result:', targetResult);
      console.log('All results:', results);
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        console.log('Calling onContentSelect with matched content');

        // Get all matching content IDs from the current results, not from findResults
        const allIds: string[] = [];
          results.forEach(result => {
            if (result.matchedContents) {
              result.matchedContents.forEach(content => {
                allIds.push(content.id);
              });
            }
          });

        setCurrentSearchMatchingIds(allIds);

        onContentSelect(
          targetResult.category.id,
          targetResult.matchedContents[0].id,
          findSearchString,
          parseSearchTerms(findSearchString),
          searchMode,
          wholeWord,
          caseSensitive,
          allIds
        );
      }
    } else {
      // Show warning in dialog instead of alert
      setShowEndWarning(true);
      setTimeout(() => setShowEndWarning(false), 3000);
    }
  };

  const handleFindNext = () => {
    if (findResults.length === 0) return;
    
    const currentResult = findResults[currentFindIndex];
    
    // If current result has multiple content matches, navigate to next content first
    if (currentResult.matchedContents && currentResult.matchedContents.length > 1) {
      const nextContentIndex = currentContentIndex + 1;
      
      if (nextContentIndex < currentResult.matchedContents.length) {
        // Move to next content in same category
        setCurrentContentIndex(nextContentIndex);
        setShowEndWarning(false);
        setShowStartWarning(false);
        setHasWrappedAround(false);
        
        if (onContentSelect) {
          onContentSelect(currentResult.category.id, currentResult.matchedContents[nextContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
        }
        return;
      } else {
        // No more content in this category, reset content index and move to next category
        setCurrentContentIndex(0);
      }
    }
    
    // Move to next category
    const nextIndex = currentFindIndex + 1;
    
    if (nextIndex >= findResults.length) {
      // Reached the end, automatically continue from the top
      setShowEndWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowEndWarning(false), 3000); // Hide after 3 seconds
      setCurrentFindIndex(0);
      setCurrentContentIndex(0);
      const targetResult = findResults[0];
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(nextIndex);
      setCurrentContentIndex(0);
      const targetResult = findResults[nextIndex];
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    }
  };

  const handleFindPrevious = () => {
    if (findResults.length === 0) return;
    
    const currentResult = findResults[currentFindIndex];
    
    // If current result has multiple content matches, navigate to previous content first
    if (currentResult.matchedContents && currentResult.matchedContents.length > 1) {
      const prevContentIndex = currentContentIndex - 1;
      
      if (prevContentIndex >= 0) {
        // Move to previous content in same category
        setCurrentContentIndex(prevContentIndex);
        setShowEndWarning(false);
        setShowStartWarning(false);
        setHasWrappedAround(false);
        
        if (onContentSelect) {
          onContentSelect(currentResult.category.id, currentResult.matchedContents[prevContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
        }
        return;
      } else {
        // No more content in this category, move to previous category and set to last content
        const prevIndex = currentFindIndex - 1;
        if (prevIndex >= 0) {
          const prevResult = findResults[prevIndex];
          const lastContentIndex = prevResult.matchedContents ? prevResult.matchedContents.length - 1 : 0;
          setCurrentFindIndex(prevIndex);
          setCurrentContentIndex(lastContentIndex);
          onCategorySelect(prevResult.category);
          onExpandParents(prevResult.category);
          if (prevResult.matchedContents && onContentSelect) {
            onContentSelect(prevResult.category.id, prevResult.matchedContents[lastContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
          return;
        }
      }
    }
    
    // Move to previous category
    const prevIndex = currentFindIndex - 1;
    
    if (prevIndex < 0) {
      // Reached the beginning, automatically continue from the end
      setShowStartWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowStartWarning(false), 3000); // Hide after 3 seconds
      const lastIndex = findResults.length - 1;
      setCurrentFindIndex(lastIndex);
      const targetResult = findResults[lastIndex];
      const lastContentIndex = targetResult.matchedContents ? targetResult.matchedContents.length - 1 : 0;
      setCurrentContentIndex(lastContentIndex);
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[lastContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(prevIndex);
      const targetResult = findResults[prevIndex];
      const lastContentIndex = targetResult.matchedContents ? targetResult.matchedContents.length - 1 : 0;
      setCurrentContentIndex(lastContentIndex);
      onCategorySelect(targetResult.category);
      onExpandParents(targetResult.category);
      // Select content if there's a match
      if (targetResult.matchedContents && onContentSelect) {
        onContentSelect(targetResult.category.id, targetResult.matchedContents[lastContentIndex].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
      }
    }
  };

  // Helper function to get all matching content IDs from all search results
  const getAllMatchingContentIds = (): string[] => {
    const allIds: string[] = [];
    findResults.forEach(result => {
      if (result.matchedContents) {
        result.matchedContents.forEach(content => {
          allIds.push(content.id);
        });
      }
    });
    return allIds;
  };

  // Helper function to calculate total matches including content matches
  const getTotalMatches = () => {
    let total = 0;
    findResults.forEach(result => {
      if (result.matchedContents) {
        total += result.matchedContents.length;
      } else {
        total += 1; // Category match (name or description)
      }
    });
    return total;
  };

  // Helper function to get current match number
  const getCurrentMatchNumber = () => {
    let current = 0;
    for (let i = 0; i < currentFindIndex; i++) {
      const result = findResults[i];
      if (result.matchedContents) {
        current += result.matchedContents.length;
      } else {
        current += 1;
      }
    }
    // Add current category's matches
    const currentResult = findResults[currentFindIndex];
    if (currentResult.matchedContents) {
      current += currentContentIndex + 1;
    } else {
      current += 1;
    }
    return current;
  };

  const handleDialogClose = () => {
    setFindSearchString('');
    setFindResults([]);
    setCurrentFindIndex(0);
    setFindStarted(false);
    setShowEndWarning(false);
    setShowStartWarning(false);
    setHasWrappedAround(false);
    setSearchInDescriptions(false);
    setSearchMode('any');
    setWholeWord(false);
    setCaseSensitive(false);
    // Reset position to initial top-right position
    setDialogPosition({ x: Math.max(0, window.innerWidth - 350), y: 80 });
    onClose();
  };

  // Dialog drag handlers
  const handleDialogMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    
    // Check if the click is on the header area
    const isHeader = target.closest('[style*="background-color: rgb(240, 240, 240)"]') || 
                    target.closest('[style*="background-color: #f0f0f0"]') ||
                    target.style.backgroundColor === 'rgb(240, 240, 240)' ||
                    target.style.backgroundColor === '#f0f0f0';
    
    if (isHeader) {
      e.preventDefault();
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleDialogMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep dialog within viewport bounds
      const maxX = window.innerWidth - 400; // dialog width
      const maxY = window.innerHeight - 300; // dialog height
      
      setDialogPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleDialogMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDialogMouseMove);
      document.addEventListener('mouseup', handleDialogMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleDialogMouseMove);
        document.removeEventListener('mouseup', handleDialogMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      
      // Prevent shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Ctrl+F or Cmd+F: Focus search input
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Enter"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
      
      // Enter: Perform search
      if (e.key === 'Enter') {
        e.preventDefault();
        handleFindSearch();
      }
      
      // F3 or Ctrl+G: Find next
      if (e.key === 'F3' || ((e.ctrlKey || e.metaKey) && e.key === 'g')) {
        e.preventDefault();
        handleFindNext();
      }
      
      // Shift+F3 or Ctrl+Shift+G: Find previous
      if ((e.shiftKey && e.key === 'F3') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G')) {
        e.preventDefault();
        handleFindPrevious();
      }
      
      // Ctrl+Shift+F3 or Ctrl+Shift+Alt+G: Next category
      if ((e.ctrlKey && e.shiftKey && e.key === 'F3') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.altKey && e.key === 'G')) {
        e.preventDefault();
        const nextIndex = currentFindIndex + 1;
        if (nextIndex >= findResults.length) {
          setCurrentFindIndex(0);
          setCurrentContentIndex(0);
          const targetResult = findResults[0];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        } else {
          setCurrentFindIndex(nextIndex);
          setCurrentContentIndex(0);
          const targetResult = findResults[nextIndex];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        }
      }
      
      // Ctrl+Alt+F3 or Ctrl+Alt+G: Previous category
      if ((e.ctrlKey && e.altKey && e.key === 'F3') || ((e.ctrlKey || e.metaKey) && e.altKey && e.key === 'G')) {
        e.preventDefault();
        const prevIndex = currentFindIndex - 1;
        if (prevIndex < 0) {
          const lastIndex = findResults.length - 1;
          setCurrentFindIndex(lastIndex);
          setCurrentContentIndex(0);
          const targetResult = findResults[lastIndex];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        } else {
          setCurrentFindIndex(prevIndex);
          setCurrentContentIndex(0);
          const targetResult = findResults[prevIndex];
          onCategorySelect(targetResult.category);
          onExpandParents(targetResult.category);
          if (targetResult.matchedContents && onContentSelect) {
            onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
          }
        }
      }
      
      // Escape: Close dialog
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDialogClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, findResults.length, currentFindIndex]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: dialogPosition.x,
        top: dialogPosition.y,
        zIndex: 1300,
        backgroundColor: 'white',
        borderRadius: 2,
        boxShadow: 3,
        border: '1px solid #ddd',
        p: 1,
        minWidth: 280,
        maxWidth: 350,
        userSelect: 'none',
      }}
    >
      {/* Draggable Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 1,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' }
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
          const rect = e.currentTarget.getBoundingClientRect();
          setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          });
        }}
      >
        <Typography variant="subtitle1" sx={{ flexGrow: 1, userSelect: 'none', fontSize: '0.9rem' }}>
          Find in Document
        </Typography>
        <Tooltip title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Keyboard Shortcuts:</Typography>
            <Typography variant="caption" component="div">Ctrl+F / Cmd+F: Focus search</Typography>
            <Typography variant="caption" component="div">Enter: Search</Typography>
            <Typography variant="caption" component="div">F3 / Ctrl+G: Find next content</Typography>
            <Typography variant="caption" component="div">Shift+F3 / Ctrl+Shift+G: Find previous content</Typography>
            <Typography variant="caption" component="div">Ctrl+Shift+F3 / Ctrl+Shift+Alt+G: Next category</Typography>
            <Typography variant="caption" component="div">Ctrl+Alt+F3 / Ctrl+Alt+G: Previous category</Typography>
            <Typography variant="caption" component="div">Escape: Close dialog</Typography>
          </Box>
        }>
          <IconButton size="small" sx={{ mr: 0.5 }}>
            <HelpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {searchMode === 'semantic' && (
          <Tooltip title={getTooltipTextForState('find-semantic-settings', true)}>
            <IconButton 
              size="small" 
              onClick={() => setShowFindSettings(!showFindSettings)}
              sx={{ mr: 0.5 }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={getTooltipTextForState('find-close', true)}>
          <IconButton 
            size="small" 
            onClick={handleDialogClose}
            sx={{ userSelect: 'none' }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Dialog Content */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={
            searchMode === 'exact' 
              ? 'Exact phrase...' 
              : searchMode === 'all'
              ? 'All words...'
              : 'Any words...'
          }
          value={findSearchString}
          onChange={(e) => setFindSearchString(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleFindSearch()}
          sx={{ flexGrow: 1 }}
          autoFocus
          title={getTooltipTextForState('find-search-input', true)}
        />
        <Tooltip title={getTooltipTextForState('find-search', true)}>
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={handleFindSearch}
              disabled={!findSearchString.trim() || isSemanticSearching}
              sx={{ height: '40px' }} // Match TextField small size height
            >
              {isSemanticSearching ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Searching...
                </Box>
              ) : (
                'Find'
              )}
            </Button>
          </span>
        </Tooltip>
      </Box>
      
      {/* Search Options */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Search options
          </Typography>
          <Tooltip title={getTooltipTextForState('find-search-options-toggle', true)}>
            <IconButton
              size="small"
              onClick={() => setSearchOptionsCollapsed(!searchOptionsCollapsed)}
              sx={{ p: 0.5 }}
            >
              {searchOptionsCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        
        {!searchOptionsCollapsed && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Search in:
            </Typography>
            <Tooltip title={getTooltipTextForState('find-search-names', true)}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchInNames}
                      onChange={(e) => setSearchInNames(e.target.checked)}
                      size="small"
                      disabled={searchMode === 'semantic'}
                    />
                  }
                  label={
                    <Tooltip title={getTooltipTextForState('find-search-names', true)}>
                      <span>Category names</span>
                    </Tooltip>
                  }
                />
              </span>
            </Tooltip>
            <Tooltip title={getTooltipTextForState('find-search-content', true)}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchInContent}
                      onChange={(e) => setSearchInContent(e.target.checked)}
                      size="small"
                      disabled={searchMode === 'semantic'}
                    />
                  }
                  label="Content"
                />
              </span>
            </Tooltip>
            <Tooltip title={getTooltipTextForState('find-search-descriptions', true)}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={searchInDescriptions}
                      onChange={(e) => setSearchInDescriptions(e.target.checked)}
                      size="small"
                      disabled={searchMode === 'semantic'}
                    />
                  }
                  label="Descriptions"
                />
              </span>
            </Tooltip>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Match options:
              </Typography>
              <Tooltip title={getTooltipTextForState('find-whole-word', true)}>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={wholeWord}
                        onChange={(e) => setWholeWord(e.target.checked)}
                        size="small"
                        disabled={searchMode === 'semantic'}
                      />
                    }
                    label="Whole word only"
                  />
                </span>
              </Tooltip>
              <Tooltip title={getTooltipTextForState('find-case-sensitive', true)}>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={caseSensitive}
                        onChange={(e) => setCaseSensitive(e.target.checked)}
                        size="small"
                        disabled={searchMode === 'semantic'}
                      />
                    }
                    label="Case sensitive"
                  />
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Search mode:
              </Typography>
              <RadioGroup
                value={searchMode}
                onChange={(e) => setSearchMode(e.target.value as 'any' | 'all' | 'exact' | 'semantic')}
                row
              >
                <Tooltip title={getTooltipTextForState('find-search-mode', true)}>
                  <span>
                    <FormControlLabel
                      value="any"
                      control={<Radio size="small" />}
                      label="Any word (OR)"
                    />
                  </span>
                </Tooltip>
                <Tooltip title={getTooltipTextForState('find-search-mode', true)}>
                  <span>
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label="All words (AND)"
                    />
                  </span>
                </Tooltip>
                <Tooltip title={getTooltipTextForState('find-search-mode', true)}>
                  <span>
                    <FormControlLabel
                      value="exact"
                      control={<Radio size="small" />}
                      label="Exact phrase"
                    />
                  </span>
                </Tooltip>
                <Tooltip title={getTooltipTextForState('semantic-search', true)}>
                  <span>
                    <FormControlLabel
                      value="semantic"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AiSparklesIcon sx={{ fontSize: '0.875rem' }} />
                          Semantic
                        </Box>
                      }
                    />
                  </span>
                </Tooltip>
              </RadioGroup>
            </Box>
            
            {/* Subcase Filtering */}
            {documentSubcases.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Filter by subcases:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Tooltip title={getTooltipTextForState('find-subcase-filter', true)}>
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterBySubcases}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFilterBySubcases(isChecked);
                              findFilterSubcasesBaselineRef.current = null;
                              if (isChecked) {
                                setSelectedSubcaseGroups([]);
                                setSelectedIndividualSubcases([...documentSubcases]);
                              } else {
                                setSelectedSubcaseGroups([]);
                                setSelectedIndividualSubcases([]);
                              }
                            }}
                            size="small"
                          />
                        }
                        label="Filter by subcases"
                      />
                    </span>
                  </Tooltip>
                  {filterBySubcases && (
                    <Tooltip title={getTooltipTextForState('find-subcase-selection-toggle', true)}>
                      <IconButton
                        size="small"
                        onClick={() => setSubcaseSelectionCollapsed(!subcaseSelectionCollapsed)}
                        sx={{ p: 0.5 }}
                      >
                        {subcaseSelectionCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                {filterBySubcases && !subcaseSelectionCollapsed && (
                  <Box sx={{ mt: 1, ml: 3 }}>
                    {/* Subcase Groups */}
                    {subcaseGroups.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Subcase Groups:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {subcaseGroups.map(group => (
                            <Chip
                              key={group.name}
                              label={group.name}
                              size="small"
                              color={selectedSubcaseGroups.includes(group.name) ? 'primary' : 'default'}
                              onClick={() => handleFindSubcaseGroupSelect(group.name)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Individual Subcases */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Individual Subcases:
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedIndividualSubcases.length === documentSubcases.length}
                            indeterminate={
                              selectedIndividualSubcases.length > 0 &&
                              selectedIndividualSubcases.length < documentSubcases.length
                            }
                            onChange={e => {
                              findFilterSubcasesBaselineRef.current = null;
                              if (e.target.checked) {
                                setSelectedIndividualSubcases([...documentSubcases]);
                                setSelectedSubcaseGroups([]);
                              } else {
                                setSelectedIndividualSubcases([]);
                                setSelectedSubcaseGroups([]);
                              }
                            }}
                            size="small"
                          />
                        }
                        label="Select All"
                      />
                      <Box sx={{ maxHeight: 120, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                        {documentSubcases.map((subcase: string) => (
                          <FormControlLabel
                            key={subcase}
                            control={
                              <Checkbox
                                checked={selectedIndividualSubcases.includes(subcase)}
                                onChange={(e) => {
                                  setSelectedIndividualSubcases(prev =>
                                    e.target.checked
                                      ? [...prev, subcase]
                                      : prev.filter(s => s !== subcase)
                                  );
                                }}
                                size="small"
                              />
                            }
                            label={subcase}
                            sx={{ display: 'block', mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {/* Selected subcases summary - always show when filter is active */}
                {filterBySubcases && (() => {
                  const selectedSubcases = getSelectedSubcases();
                  if (selectedSubcases.length > 0) {
                    return (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 3 }}>
                        Filtering by {selectedSubcases.length} subcase{selectedSubcases.length !== 1 ? 's' : ''}: {selectedSubcases.slice(0, 3).join(', ')}
                        {selectedSubcases.length > 3 && ` +${selectedSubcases.length - 3} more`}
                      </Typography>
                    );
                  }
                  return null;
                })()}
              </Box>
            )}
          </>
        )}
      </Box>
      
      {/* Semantic Search Settings */}
      {searchMode === 'semantic' && showFindSettings && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiSparklesIcon sx={{ fontSize: '1rem' }} />
            Semantic Search Settings
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography gutterBottom variant="caption">
              Similarity Threshold: {findSimilarityThreshold.toFixed(2)}
            </Typography>
            <Slider
              value={findSimilarityThreshold}
              onChange={(_, value) => updateSettings({ findSimilarityThreshold: value as number })}
              onChangeCommitted={async (_, value) => {
                updateSettings({ findSimilarityThreshold: value as number });
                try {
                  await api.put('/search/settings', {
                    search: {
                      findSimilarityThreshold: value as number
                    }
                  });
                } catch (error) {
                  console.error('Error updating find settings:', error);
                }
              }}
              min={0}
              max={1}
              step={0.01}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              Lower values show more results, higher values show only very similar results
            </Typography>
          </Box>
          <Tooltip title={getTooltipTextForState('find-show-similarity-score', true)}>
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={findShowSimilarityScore}
                    onChange={async (e) => {
                      updateSettings({ findShowSimilarityScore: e.target.checked });
                      try {
                        await api.put('/search/settings', {
                          search: {
                            findShowSimilarityScore: e.target.checked
                          }
                        });
                      } catch (error) {
                        console.error('Error updating find settings:', error);
                      }
                    }}
                    size="small"
                  />
                }
                label="Show similarity scores in results"
              />
            </span>
          </Tooltip>
        </Box>
      )}
      
      {findStarted && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {findResults.length > 0 
              ? `Found ${getTotalMatches()} match(es) - ${getCurrentMatchNumber()} of ${getTotalMatches()}`
              : 'No results found'
            }
            {findResults.length > 0 && (
              <span style={{ fontSize: '0.75em', opacity: 0.7 }}>
                {' '}({searchMode === 'any' ? 'OR' : searchMode === 'all' ? 'AND' : searchMode === 'exact' ? 'Exact' : 'Semantic'})
                {caseSensitive && ' • Case sensitive'}
              </span>
            )}
          </Typography>
          {findResults.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Current: {findResults[currentFindIndex].category.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(() => {
                  const matchedContents = findResults[currentFindIndex].matchedContents;
                  if (matchedContents) {
                    return `Match found in content${matchedContents.length > 1 ? ` (${currentContentIndex + 1} of ${matchedContents.length})` : ''}`;
                  } else if (searchInDescriptions && findResults[currentFindIndex].category.description && 
                    checkTextMatch(findResults[currentFindIndex].category.description || '', parseSearchTerms(findSearchString))) {
                    return 'Match found in description';
                  } else if (searchInNames && checkTextMatch(findResults[currentFindIndex].category.name, parseSearchTerms(findSearchString))) {
                    return 'Match found in name';
                  } else {
                    return 'Match found';
                  }
                })()}
                {/* Show similarity score for semantic search */}
                {searchMode === 'semantic' && findShowSimilarityScore && findResults[currentFindIndex].score !== undefined && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                    Similarity: {findResults[currentFindIndex].score!.toFixed(1)}%
                  </Typography>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      
      {/* Warning messages */}
      {showEndWarning && (
        <Box sx={{ mb: 2, p: 1, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 1 }}>
          <Typography variant="body2" color="#856404">
            {hasWrappedAround ? 'Continued from the top.' : 'Reached the end of search results.'}
          </Typography>
        </Box>
      )}
      
      {showStartWarning && (
        <Box sx={{ mb: 2, p: 1, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 1 }}>
          <Typography variant="body2" color="#856404">
            {hasWrappedAround ? 'Continued from the bottom.' : 'Reached the beginning of search results.'}
          </Typography>
        </Box>
      )}
      
      {findResults.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Content Navigation - only show when content search is enabled or semantic search is used */}
          {(searchInContent || searchMode === 'semantic') && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={getTooltipTextForState('find-previous', true)}>
                <span>
                  <Button
                    size="small"
                    onClick={handleFindPrevious}
                    disabled={(() => {
                      if (findResults.length > 1) return false;
                      const matchedContents = findResults[currentFindIndex].matchedContents;
                      return !matchedContents || matchedContents.length <= 1;
                    })()}
                    startIcon={<KeyboardArrowUpIcon />}
                    variant="outlined"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      width: '100px'
                    }}
                  >
                    Prev Content
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={getTooltipTextForState('find-next', true)}>
                <span>
                  <Button
                    size="small"
                    onClick={handleFindNext}
                    disabled={(() => {
                      if (findResults.length > 1) return false;
                      const matchedContents = findResults[currentFindIndex].matchedContents;
                      return !matchedContents || matchedContents.length <= 1;
                    })()}
                    endIcon={<KeyboardArrowDownIcon />}
                    variant="outlined"
                    sx={{ 
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      width: '100px'
                    }}
                  >
                    Next Content
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
          
          {/* Category Navigation */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={getTooltipTextForState('find-previous-category', true)}>
              <span>
                <Button
                  size="small"
                  onClick={() => {
                    // Navigate to previous category
                    const prevIndex = currentFindIndex - 1;
                    if (prevIndex < 0) {
                      // Wrap around to the end
                      const lastIndex = findResults.length - 1;
                      setCurrentFindIndex(lastIndex);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[lastIndex];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    } else {
                      setCurrentFindIndex(prevIndex);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[prevIndex];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    }
                  }}
                  disabled={findResults.length <= 1}
                  startIcon={<KeyboardArrowUpIcon />}
                  variant="contained"
                  color="primary"
                  sx={{ 
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    width: '100px'
                  }}
                >
                  Prev Category
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={getTooltipTextForState('find-next-category', true)}>
              <span>
                <Button
                  size="small"
                  onClick={() => {
                    // Navigate to next category
                    const nextIndex = currentFindIndex + 1;
                    if (nextIndex >= findResults.length) {
                      // Wrap around to the beginning
                      setCurrentFindIndex(0);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[0];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    } else {
                      setCurrentFindIndex(nextIndex);
                      setCurrentContentIndex(0);
                      const targetResult = findResults[nextIndex];
                      onCategorySelect(targetResult.category);
                      onExpandParents(targetResult.category);
                      if (targetResult.matchedContents && onContentSelect) {
                        onContentSelect(targetResult.category.id, targetResult.matchedContents[0].id, findSearchString, searchMode === 'semantic' ? [] : parseSearchTerms(findSearchString), searchMode, wholeWord, caseSensitive, currentSearchMatchingIds);
                      }
                    }
                  }}
                  disabled={findResults.length <= 1}
                  endIcon={<KeyboardArrowDownIcon />}
                  variant="contained"
                  color="primary"
                  sx={{ 
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.75rem',
                    width: '100px'
                  }}
                >
                  Next Category
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FindCategoryDialog;